import { Suspense, lazy, useEffect, useId, useRef, useState } from 'react';
import { ApiFailure, api } from '../lib/api';
import type { PickupPointRow, ZoneRow } from '../lib/types';
import { AddressSearchBox } from './AddressSearchBox';
import { X } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

// 地图选点会拉进整个 maplibre(250KB)—— 懒加载,
// 否则只是打开设置页也要下载地图库
const LocationPicker = lazy(() =>
  import('./LocationPicker').then((m) => ({ default: m.LocationPicker })),
);

interface Form {
  code: string;
  name: string;
  type: string;
  address: string;
  addressDetail: string;
  lat: number | null;
  lng: number | null;
  contactName: string;
  contactPhone: string;
  cutoffTime: string;
  pickupInstruction: string;
  parkingNote: string;
  geofenceRadiusM: number;
  requireGeofence: boolean;
  zoneId: string;
}

export function PickupPointForm({
  existing,
  zones,
  onClose,
  onSaved,
}: {
  existing?: PickupPointRow;
  zones: ZoneRow[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [form, setForm] = useState<Form>({
    code: existing?.code ?? '',
    name: existing?.name ?? '',
    type: existing?.type ?? 'MERCHANT',
    address: existing?.address ?? '',
    /*
     * ★ 这两个原来没有回填:
     *
     *   - `requireGeofence` 写死成 `true` —— 用户在界面上取消勾选、保存,
     *     库里**确实存成了 false**,可再打开又是勾上的:
     *     看起来像「保存没生效」,而其实是表单没读已有的值。
     *   - `addressDetail` 初始化成空串 —— 每次编辑保存都会把它**清空**,
     *     而且悄无声息(门牌、楼层就这么没了)。
     *
     *   同一个毛病的两种后果:一个让人以为没保存,一个真的丢数据。
     */
    addressDetail: existing?.addressDetail ?? '',
    lat: existing?.lat ?? null,
    lng: existing?.lng ?? null,
    contactName: existing?.contactName ?? '',
    contactPhone: existing?.contactPhone ?? '',
    cutoffTime: existing?.cutoffTime ?? '',
    pickupInstruction: existing?.pickupInstruction ?? '',
    parkingNote: existing?.parkingNote ?? '',
    geofenceRadiusM: existing?.geofenceRadiusM ?? 200,
    requireGeofence: existing?.requireGeofence ?? true,
    // ★ 区域也要回填 —— 不填的话编辑框里永远显示「未指定」,
    //   而这个取货点明明属于某个区。人看到「未指定」会去重新选一遍,
    //   而那一下才是真的改动
    zoneId: existing?.zone?.publicId ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  /** 人动过表单没有。ref 是给异步回调看的 —— 它拿不到最新的 state */
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);

  /*
   * ★ 打开时**现取一份**,不信任列表里那份。
   *
   *   设置页的取货点列表是进页面时加载的,可能已经放了几分钟 ——
   *   期间别人改过,或者自己刚在这个弹窗里改完又打开了一次。
   *   拿旧的填表单,人看到的是已经不成立的值;而一按保存就把它写回去,
   *   **刚改的东西被自己覆盖掉,没有任何提示**。
   *
   *   拉取失败就保持列表里那份 —— 只读不到最新的,总好过打不开这张表单。
   */
  useEffect(() => {
    if (!existing?.id) return;
    let cancelled = false;
    // ★ 回来得晚也不能盖掉人已经改的东西 —— 那就成了另一种「保存了个寂寞」:
    //   刚取消的勾在半秒后自己又勾上了,而人已经在按保存了。
    if (dirty) return;
    api<PickupPointRow>(`/pickup-points/${existing.id}`)
      .then((fresh) => {
        if (cancelled || dirtyRef.current) return;
        setForm((f) => ({
          ...f,
          code: fresh.code ?? f.code,
          name: fresh.name ?? f.name,
          type: fresh.type ?? f.type,
          address: fresh.address ?? f.address,
          addressDetail: fresh.addressDetail ?? '',
          lat: fresh.lat ?? null,
          lng: fresh.lng ?? null,
          contactName: fresh.contactName ?? '',
          contactPhone: fresh.contactPhone ?? '',
          cutoffTime: fresh.cutoffTime ?? '',
          pickupInstruction: fresh.pickupInstruction ?? '',
          parkingNote: fresh.parkingNote ?? '',
          geofenceRadiusM: fresh.geofenceRadiusM ?? 200,
          requireGeofence: fresh.requireGeofence ?? true,
          zoneId: fresh.zone?.publicId ?? '',
        }));
      })
      .catch(() => {
        /* 读不到就用列表里那份 */
      });
    return () => {
      cancelled = true;
    };
    // dirty 只用来「一旦人动过就别再拉了」,不该作为依赖重新触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => {
    dirtyRef.current = true;
    setDirty(true);
    setForm((f) => ({ ...f, [k]: v }));
  };

  async function save() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        ...form,
        zoneId: form.zoneId || undefined,
        cutoffTime: form.cutoffTime || undefined,
        addressDetail: form.addressDetail || undefined,
        contactName: form.contactName || undefined,
        contactPhone: form.contactPhone || undefined,
        pickupInstruction: form.pickupInstruction || undefined,
        parkingNote: form.parkingNote || undefined,
      };

      if (existing) {
        const r = await api<{ staleSnapshotOrders: number }>(`/pickup-points/${existing.id}`, {
          method: 'PATCH',
          body,
        });
        // 快照不回溯 —— 但要告诉调度员有多少未取货的单还带着旧地址
        if (r.staleSnapshotOrders > 0) {
          setWarning(
            `已保存。注意:有 ${r.staleSnapshotOrders} 单尚未取货,它们仍使用修改前的地址快照。`,
          );
          setBusy(false);
          return;
        }
      } else {
        await api('/pickup-points', { method: 'POST', body });
      }
      await onSaved();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  }

  if (warning) {
    return (
      <Modal title="已保存" onClose={onClose}>
        <p className="text-amber-700 dark:text-amber-400">{warning}</p>
        <button
          onClick={() => void onSaved()}
          className="mt-4 w-full h-10 rounded-lg bg-brand-600 text-white font-medium"
        >
          知道了
        </button>
      </Modal>
    );
  }

  return (
    <Modal title={existing ? '编辑取货点' : '新建取货点'} onClose={onClose} wide>
      {error && (
        <div className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 px-4 py-2 text-red-700
                        dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="编码"
              value={form.code}
              onChange={(v) => set('code', v)}
              disabled={Boolean(existing)}
              hint={existing ? '编码不可修改' : 'Excel 导入用它对齐'}
            />
            <Select
              label="类型"
              value={form.type}
              onChange={(v) => set('type', v)}
              options={[
                ['MERCHANT', '商家店铺'],
                ['WAREHOUSE', '仓库'],
                ['HUB', '中转点'],
                ['LOCKER', '自提柜'],
              ]}
            />
          </div>

          <Field label="名称" value={form.name} onChange={(v) => set('name', v)} />
          <Field label="地址" value={form.address} onChange={(v) => set('address', v)} />
          {/*
            ★ 和下单页同一个搜索框 —— 取货点没有坐标的后果比订单更重:
              订单缺坐标只是这一单上不了地图,而取货点缺坐标意味着
              **挂在它下面的每一单**司机都导航不过去,
              而且「按取货点分组」的地图视图会整块空掉。
          */}
          <AddressSearchBox
            query={form.address}
            onPick={(c) => setForm((f) => ({ ...f, address: c.address, lat: c.lat, lng: c.lng }))}
          />
          <Field
            label="门牌 / 楼层"
            value={form.addressDetail}
            onChange={(v) => set('addressDetail', v)}
          />

          <div className="grid grid-cols-2 gap-3">
            <Field
              label="联系人"
              value={form.contactName}
              onChange={(v) => set('contactName', v)}
            />
            <Field
              label="联系电话"
              value={form.contactPhone}
              onChange={(v) => set('contactPhone', v)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Select
              label="所属区域"
              value={form.zoneId}
              onChange={(v) => set('zoneId', v)}
              options={[['', '不指定'], ...zones.map((z) => [z.id, z.name] as [string, string])]}
            />
            <Field
              label="截单时间"
              value={form.cutoffTime}
              onChange={(v) => set('cutoffTime', v)}
              placeholder="16:00"
            />
          </div>
        </div>

        <div className="space-y-3">
          {/* ★ 这三样是新司机第一次去时最需要的,不要当成可选项 */}
          <TextArea
            label="取货说明"
            value={form.pickupInstruction}
            onChange={(v) => set('pickupInstruction', v)}
            placeholder="从后门进,找王经理,需登记身份证"
            hint="司机第一次来时最需要这条"
          />
          <TextArea
            label="停车提示"
            value={form.parkingNote}
            onChange={(v) => set('parkingNote', v)}
            placeholder="门口不能停车,停对面商场 B2"
          />

          <div>
            <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">
              位置(搜地址自动带出来,也可以点地图微调)
            </label>
            <Suspense
              fallback={
                <div className="h-56 rounded-lg border border-slate-200 dark:border-slate-700
                                grid place-items-center text-slate-400 text-sm">
                  地图加载中…
                </div>
              }
            >
              <LocationPicker
                lat={form.lat}
                lng={form.lng}
                onPick={(lat, lng) => setForm((f) => ({ ...f, lat, lng }))}
              />
            </Suspense>
            <p className="mt-1 text-xs text-slate-400">
              {form.lat != null
                ? `${form.lat.toFixed(6)}, ${form.lng!.toFixed(6)}`
                : '未选择 —— 没有坐标司机端地图上看不到这个点'}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                checked={form.requireGeofence}
                onChange={(e) => set('requireGeofence', e.target.checked)}
              />
              取货需在范围内
            </label>
            <input
              type="number"
              value={form.geofenceRadiusM}
              onChange={(e) => set('geofenceRadiusM', Number(e.target.value))}
              className="w-24 h-9 px-2 rounded-lg border border-slate-300 dark:border-slate-700
                         bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            />
            <span className="text-sm text-slate-500">米</span>
          </div>
        </div>
      </div>

      <div className="mt-5 flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 h-10 rounded-lg border border-slate-300 dark:border-slate-600
                     text-slate-700 dark:text-slate-300"
        >
          取消
        </button>
        <button
          onClick={() => void save()}
          disabled={busy || !form.code || !form.name || !form.address}
          className="flex-1 h-10 rounded-lg bg-brand-600 text-white font-medium disabled:opacity-40"
        >
          {busy ? '保存中…' : '保存'}
        </button>
      </div>
    </Modal>
  );
}

export function ZoneForm({
  existing,
  drivers,
  zones,
  onClose,
  onSaved,
}: {
  existing?: ZoneRow;
  drivers: Array<{ id: string; name: string }>;
  zones: ZoneRow[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [form, setForm] = useState({
    code: existing?.code ?? '',
    name: existing?.name ?? '',
    priority: existing?.priority ?? 100,
    isRemote: existing?.isRemote ?? false,
    remoteSurcharge: existing ? Number(existing.remoteSurcharge) : 0,
    defaultDriverId: existing?.defaultDriver?.publicId ?? '',
    parentId: existing?.parent?.id ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      // ★ 上级区域清空要传 null,不是 undefined —— undefined 在后端是「这次不改」,
      //   于是「取消继承」这个操作会静静地什么都不做
      const body = {
        ...form,
        defaultDriverId: form.defaultDriverId || undefined,
        parentId: form.parentId || null,
      };
      if (existing) {
        await api(`/zones/${existing.id}`, { method: 'PATCH', body });
      } else {
        await api('/zones', { method: 'POST', body });
      }
      await onSaved();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={existing ? '编辑区域' : '新建区域'} onClose={onClose}>
      {error && (
        <div className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 px-4 py-2 text-red-700
                        dark:text-red-300 text-sm">
          {error}
        </div>
      )}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="编码"
            value={form.code}
            onChange={(v) => setForm({ ...form, code: v })}
            disabled={Boolean(existing)}
          />
          <Field
            label="名称"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
          />
        </div>

        <div>
          <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">优先级</label>
          <input
            type="number"
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
            className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700
                       bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
          />
          <p className="mt-1 text-xs text-slate-400">
            只影响列表里的排序(数值小的排前面),判区时不看它。地址同时命中两个价钱不一样的区时,订单会进复核让人选;要彻底分清,给区「画范围」
          </p>
        </div>

        <label className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            checked={form.isRemote}
            onChange={(e) => setForm({ ...form, isRemote: e.target.checked })}
          />
          偏远地区
        </label>

        {form.isRemote && (
          <div>
            <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">
              偏远附加费(分)
            </label>
            <input
              type="number"
              value={form.remoteSurcharge}
              onChange={(e) => setForm({ ...form, remoteSurcharge: Number(e.target.value) })}
              className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700
                         bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            />
            <p className="mt-1 text-xs text-slate-400">
              以分为单位。500 = 5.00 元
            </p>
          </div>
        )}

        <Select
          label="上级区域"
          value={form.parentId}
          onChange={(v) => setForm({ ...form, parentId: v })}
          options={[
            ['', '不设(自己独立计价)'],
            ...zones
              // 自己不能当自己的上级
              .filter((z) => z.id !== existing?.id)
              .map((z) => [z.id, `${z.code} ${z.name}`] as [string, string]),
          ]}
        />
        <p className="-mt-2 text-xs text-slate-400">
          这一片没配计价规则时,按上级区域那条算。
          {form.isRemote && '偏远附加费仍然只按本区域算,不会往上继承。'}
        </p>

        <Select
          label="默认司机"
          value={form.defaultDriverId}
          onChange={(v) => setForm({ ...form, defaultDriverId: v })}
          options={[['', '不指定'], ...drivers.map((d) => [d.id, d.name] as [string, string])]}
        />
      </div>

      <div className="mt-5 flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 h-10 rounded-lg border border-slate-300 dark:border-slate-600
                     text-slate-700 dark:text-slate-300"
        >
          取消
        </button>
        <button
          onClick={() => void save()}
          disabled={busy || !form.code || !form.name}
          className="flex-1 h-10 rounded-lg bg-brand-600 text-white font-medium disabled:opacity-40"
        >
          {busy ? '保存中…' : '保存'}
        </button>
      </div>
    </Modal>
  );
}

function Modal({
  title,
  children,
  onClose,
  wide,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        className={`w-full ${wide ? 'max-w-4xl' : 'max-w-md'} max-h-[90dvh] overflow-y-auto
                    rounded-2xl bg-white dark:bg-slate-800 shadow-2xl p-6`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
          <button onClick={onClose} aria-label="关闭" className="text-slate-400 text-xl"><InlineIcon icon={X} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  hint,
  placeholder,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  /*
   * ★ label 要和输入框**真的关联**上。
   *
   *   原来只是并排放着 —— 既没有 `htmlFor` 也没有嵌套,于是:
   *   读屏念到这一栏时只会说「编辑框」,不说它叫什么;
   *   点标签也不会聚焦到输入框;用例更是定位不到。
   *   一张表单十几个字段,全都如此。
   */
  const id = useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm text-slate-600 dark:text-slate-400 mb-1"
      >
        {label}
      </label>
      <input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700
                   bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100
                   disabled:opacity-50 disabled:bg-slate-50 dark:disabled:bg-slate-900"
      />
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

function TextArea({
  label,
  value,
  onChange,
  hint,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={2}
        className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700
                   bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 resize-none"
      />
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<[string, string]>;
}) {
  return (
    <div>
      <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700
                   bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </div>
  );
}
